import { Check } from "lucide-react";
import React, { useMemo } from "react";

const DAY_W = 26;
const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const between = (a, b) => Math.round((b - a) / 86400000);
const COLOR = { high: "var(--wf-err)", low: "var(--wf-ok)", medium: "var(--wf-warn)" };

/** One bar per item from start to due date, colored by priority; done items faded with a check. */
export function GanttChart({ tasks, onPick }) {
  const rows = useMemo(
    () => tasks.filter((t) => t.start_date || t.due_date).map((t) => {
      const s = toDate(t.start_date || t.due_date);
      const e = toDate(t.due_date || t.start_date);
      return { t, s, e: e < s ? s : e };
    }).sort((a, b) => a.s - b.s),
    [tasks],
  );
  if (!rows.length) return <p className="text-sm text-wf-mute" data-testid="gantt-chart">Add start and due dates to items to see them on the chart.</p>;
  const first = new Date(Math.min(...rows.map((r) => r.s)));
  const span = between(first, new Date(Math.max(...rows.map((r) => r.e)))) + 1;
  const days = Array.from({ length: span }, (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i));
  const now = new Date();
  const todayIdx = between(first, new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  return (
    <div className="flex overflow-hidden rounded-xl border border-wf-line bg-wf-s2" data-testid="gantt-chart">
      <div className="w-36 shrink-0 border-r border-wf-line">
        <div className="h-9 border-b border-wf-line" />
        {rows.map(({ t }) => (
          <button key={t.id} onClick={() => onPick?.(t)} data-testid={`gantt-label-${t.id}`}
            className={`flex h-10 w-full items-center border-b border-wf-line px-2 text-left text-xs font-semibold ${t.completed_date ? "text-wf-mute line-through" : ""}`}>
            <span className="line-clamp-2">{t.title}</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <div className="relative" style={{ width: span * DAY_W }}>
          <div className="flex h-9 border-b border-wf-line">
            {days.map((d, i) => (
              <div key={i} className={`flex flex-col items-center justify-center ${d.getDay() === 1 ? "border-l border-wf-line" : ""}`} style={{ width: DAY_W }}>
                <span className="text-[10px] font-semibold text-wf-mute">{d.getDate()}</span>
                {(i === 0 || d.getDate() === 1) && <span className="text-[9px] font-bold text-wf-brand">{d.toLocaleDateString("en-US", { month: "short" })}</span>}
              </div>
            ))}
          </div>
          {rows.map(({ t, s, e }) => (
            <button key={t.id} onClick={() => onPick?.(t)} data-testid={`gantt-bar-${t.id}`} className="relative block h-10 w-full border-b border-wf-line" title={`${t.title}: ${t.start_date || t.due_date} → ${t.due_date || t.start_date}`}>
              <span className="absolute top-2.5 flex h-5 items-center justify-center rounded-md text-white"
                style={{ left: between(first, s) * DAY_W + 1, width: (between(s, e) + 1) * DAY_W - 2, background: COLOR[t.priority] ?? COLOR.medium, opacity: t.completed_date ? 0.35 : 1 }}>
                {t.completed_date && <Check size={13} />}
              </span>
            </button>
          ))}
          {todayIdx >= 0 && todayIdx < span && <div className="pointer-events-none absolute bottom-0 top-0 w-0.5 bg-wf-brand opacity-60" style={{ left: todayIdx * DAY_W + DAY_W / 2 }} />}
        </div>
      </div>
    </div>
  );
}
